import React, { useState, useEffect, useCallback, useMemo } from "react";
import { collection, query, orderBy, getDocs } from "firebase/firestore";
import { db } from "./firebase";

/**
 * ✅ Now reads from villaBookings
 * Shows Booking + Villa + Applicant + Documents
 */
const COLLECTION_NAME = "villaBookings";

const safeString = (value) => String(value || "").toLowerCase().trim();

const safeNum = (v) => {
  const n = typeof v === "string" ? parseFloat(v.toString().replace(/,/g, "")) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatINR = (num) => {
  try {
    return new Intl.NumberFormat("en-IN").format(safeNum(num));
  } catch {
    return safeNum(num).toLocaleString();
  }
};

// paise -> rupees
const paiseToRupees = (paise) => {
  const p = safeNum(paise);
  return p > 0 ? p / 100 : 0;
};

// PaymentStatus badge class
const paymentStatusClass = (status) => {
  const s = safeString(status);
  if (!s) return "unknown";
  if (s === "success" || s === "paid") return "success";
  if (s === "failed" || s === "fail") return "failed";
  if (s === "pending" || s === "processing") return "pending";
  return s.replace(/\s+/g, "-"); // safe fallback
};



const getFileExtension = (url) => {
  try {
    const extension = String(url || "").split(".").pop().split("?")[0];
    return extension || "png";
  } catch {
    return "png";
  }
};

const getDocumentTypeIcon = (type) => {
  switch (safeString(type)) {
    case "aadhaar":
    case "aadhaar2":
      return "🆔";
    case "pan":
    case "pan2":
      return "🆔";
    case "passport":
      return "🛂";
    default:
      return "📄";
  }
};

const getDocumentTypeLabel = (type) => {
  switch (safeString(type)) {
    case "aadhaar":
      return "Aadhaar Card";
    case "aadhaar2":
      return "Aadhaar Card (Applicant 2)";
    case "pan":
      return "PAN Card";
    case "pan2":
      return "PAN Card (Applicant 2)";
    case "passport":
      return "Passport";
    default:
      return type ? String(type).replace(/_/g, " ").toUpperCase() : "Document";
  }
};

// Normalize booking record into UI-friendly object
const normalizeBooking = (docId, data) => {
  const createdAt =
    data.createdAt?.toDate?.() ? data.createdAt.toDate() : data.createdAt ? new Date(data.createdAt) : null;

  const updatedAt =
    data.updatedAt?.toDate?.() ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : null;

  const applicant1 = data.applicant1 && typeof data.applicant1 === "object" ? data.applicant1 : null;
  const applicant2 = data.applicant2 && typeof data.applicant2 === "object" ? data.applicant2 : null;

  const primaryName = applicant1?.name || data.name || "N/A";
  const primaryEmail = applicant1?.email || data.email || "N/A";
  const primaryMobile = applicant1?.mobileNumber || data.mobileNumber || "N/A";
  const primaryPan = applicant1?.pan || data.pan || "N/A";

  const secondaryName = applicant2?.name || data.applicant2Name || "N/A";
  const secondaryPan = applicant2?.pan || data.applicant2Pan || "N/A";
  const hasSecondApplicant = !!(applicant2 || data.applicant2Name || data.applicant2Pan);

  const kycDocuments = Array.isArray(data.kycDocuments) ? data.kycDocuments : [];

  return {
    bookingFirestoreId: docId,

    // Primary
    name: primaryName,
    email: primaryEmail,
    mobileNumber: primaryMobile,
    pan: primaryPan,

    // Secondary
    hasSecondApplicant,
    applicant2Name: hasSecondApplicant ? secondaryName : "",
    applicant2Pan: hasSecondApplicant ? secondaryPan : "",

    // Booking
    bookingNumber: data.bookingNumber || "N/A",
    paymentStatus: data.paymentStatus || "N/A",
    source: data.source || "N/A",
    sourceDetails: data.sourceDetails || "",

    // Villa
    villaNumber: data.villaNumber || "N/A",
    villaId: data.villaId || "N/A",
    villaFirestoreId: data.villaFirestoreId || "N/A",

    configuration: data.configuration || "Unknown",
    facing: data.facing || "Unknown",
    builtUpAreaSqft: data.builtUpAreaSqft ?? "TBD",
    landAreaSqft: data.landAreaSqft ?? "TBD",
    premiumCharges: data.premiumCharges ?? "TBD",
    inclusivePrice: data.inclusivePrice ?? "TBD",

    // Advance (paise)
    advanceAmountPaise: data.advanceAmountPaise ?? 0,

    // Docs
    kycDocuments,
    totalDocuments: kycDocuments.length,

    // Dates
    createdAt,
    updatedAt,
    createdAtDate: createdAt ? createdAt.toLocaleDateString("en-IN") : "N/A",
    createdAtTS: createdAt ? createdAt.getTime() : 0,
    updatedAtDate: updatedAt ? updatedAt.toLocaleDateString("en-IN") : "N/A",
  };
};

const Users = ({ setError }) => {
  const [users, setUsers] = useState([]); // bookings
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const [downloadStatus, setDownloadStatus] = useState({ message: "", type: "" });

  // -------------------------
  // Fetch Bookings
  // -------------------------
  useEffect(() => {
    const fetchBookings = async () => {
      setUsersLoading(true);
      setUsersError(null);

      try {
        const ref = collection(db, COLLECTION_NAME);
        const q = query(ref, orderBy("createdAt", "desc"));
        const snap = await getDocs(q);

        const rows = snap.docs.map((d) => normalizeBooking(d.id, d.data()));

        // extra safety sorting
        const sorted = [...rows].sort((a, b) => (b.createdAtTS || 0) - (a.createdAtTS || 0));

        if (sorted.length === 0) setUsersError("No bookings found.");
        setUsers(sorted);
        setFilteredUsers(sorted);
      } catch (err) {
        console.error("Error fetching villaBookings:", err);
        setError?.("Failed to fetch booking data.");
        setUsersError("Failed to fetch booking data.");
      } finally {
        setUsersLoading(false);
      }
    };

    fetchBookings();
  }, [setError]);

  // -------------------------
  // Search
  // -------------------------
  const handleUserSearch = useCallback(() => {
    const q = userSearchQuery.toLowerCase().trim();
    if (!q) {
      setFilteredUsers(users);
      setUsersError(null);
      return;
    }

    const numericQuery = q.replace(/[^0-9]/g, "");

    const out = users.filter((u) => {
      const hay = [
        u.name,
        u.email,
        u.mobileNumber,
        u.pan,
        u.applicant2Name,
        u.applicant2Pan,
        u.bookingNumber,
        u.paymentStatus,
        u.source,
        u.sourceDetails,
        u.villaNumber,
        u.villaId,
        u.villaFirestoreId,
        u.configuration,
        u.facing,
        String(u.builtUpAreaSqft),
        String(u.landAreaSqft),
        String(u.inclusivePrice),
      ]
        .map((x) => safeString(x))
        .join(" | ");

      if (hay.includes(q)) return true;

      // numeric quick match for villa number/id etc.
      if (numericQuery) {
        if (safeString(u.villaId).includes(numericQuery)) return true;
        if (safeString(u.villaNumber).includes(numericQuery)) return true;
        if (safeString(u.bookingNumber).includes(numericQuery)) return true;
      }

      return false;
    });

    if (!out.length) {
      setUsersError(
        `No bookings found for "${userSearchQuery}". Try name, mobile, villa number, villaId, booking number, status, source.`
      );
    } else setUsersError(null);

    setFilteredUsers(out);
  }, [userSearchQuery, users]);

  const clearUserSearch = useCallback(() => {
    setUserSearchQuery("");
    setFilteredUsers(users);
    setUsersError(null);
  }, [users]);

  // -------------------------
  // Modals
  // -------------------------
  const openUserDetailsModal = useCallback((user) => setSelectedUser(user), []);
  const closeUserDetailsModal = useCallback(() => setSelectedUser(null), []);

  const openDocumentsModal = useCallback((documents, user) => {
    const docs = (documents || []).map((doc) => ({
      ...doc,
      applicantIndex: safeString(doc.type).includes("2") ? 2 : 1,
    }));
    setSelectedDocuments(docs);
    setSelectedUser(user);
  }, []);

  const closeDocumentsModal = useCallback(() => {
    setSelectedDocuments([]);
    setDownloadStatus({ message: "", type: "" });
  }, []);

  const openDocumentInNewTab = useCallback((url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // -------------------------
  // Downloads
  // -------------------------
  const fallbackDownload = useCallback((url, filename) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Fallback download failed:", error);
    }
  }, []);

  const handleDownload = useCallback(
    async (url, filename) => {
      try {
        setDownloadStatus({ message: `Downloading ${filename}...`, type: "success" });

        const response = await fetch(url, {
          method: "GET",
          mode: "cors",
          cache: "no-cache",
          credentials: "omit",
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const blob = await response.blob();
        if (blob.size === 0) throw new Error("Empty file received");

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        setDownloadStatus({ message: `Downloaded ${filename} successfully`, type: "success" });
        setTimeout(() => setDownloadStatus({ message: "", type: "" }), 2500);
      } catch (error) {
        console.error("Download failed:", error);
        setDownloadStatus({ message: `Failed to download ${filename}`, type: "error" });

        setTimeout(() => {
          fallbackDownload(url, filename);
          setDownloadStatus({ message: "", type: "" });
        }, 900);
      }
    },
    [fallbackDownload]
  );

  const handleDownloadAll = useCallback(async () => {
    if (selectedDocuments.length === 0) return;

    setDownloadStatus({ message: "Starting bulk download...", type: "success" });

    for (let i = 0; i < selectedDocuments.length; i++) {
      const doc = selectedDocuments[i];
      const docType = getDocumentTypeLabel(doc.type);
      const extension = getFileExtension(doc.url);
      const filename = `${docType}-${selectedUser?.name || "user"}-${Date.now()}-${i + 1}.${extension}`;

      try {
        await handleDownload(doc.url, filename);
        if (i < selectedDocuments.length - 1) {
          await new Promise((r) => setTimeout(r, 1100));
        }
      } catch (e) {
        console.error(`Failed doc ${i + 1}`, e);
      }
    }

    setDownloadStatus({
      message: `Completed downloading ${selectedDocuments.length} documents`,
      type: "success",
    });
    setTimeout(() => setDownloadStatus({ message: "", type: "" }), 2500);
  }, [selectedDocuments, selectedUser, handleDownload]);

  // Download complete bookings data as TXT
  const handleDownloadData = useCallback(() => {
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const filename = `villaBookings-complete-data-${timestamp}.txt`;

      setDownloadStatus({ message: "Generating complete data export...", type: "success" });

      let content = `VILLA BOOKINGS COMPLETE DATA EXPORT\n`;
      content += `Generated: ${new Date().toLocaleString("en-IN")}\n`;
      content += `Total Bookings: ${users.length}\n`;
      content += `======================================\n\n`;

      users.forEach((u, idx) => {
        const advRupees = paiseToRupees(u.advanceAmountPaise);

        content += `BOOKING ${idx + 1}\n`;
        content += `======================================\n`;
        content += `Booking No: ${u.bookingNumber}\n`;
        content += `Payment Status: ${u.paymentStatus}\n`;
        content += `Source: ${u.source}\n`;
        if (u.sourceDetails) content += `Source Details: ${u.sourceDetails}\n`;
        content += `Created: ${u.createdAt ? u.createdAt.toLocaleString("en-IN") : "N/A"}\n`;
        if (u.updatedAt) content += `Updated: ${u.updatedAt.toLocaleString("en-IN")}\n`;

        content += `\n--- VILLA ---\n`;
        content += `Villa Number: ${u.villaNumber}\n`;
        content += `Villa ID: ${u.villaId}\n`;
        content += `Villa FirestoreId: ${u.villaFirestoreId}\n`;
        content += `Configuration: ${u.configuration}\n`;
        content += `Facing: ${u.facing}\n`;
        content += `Built-up Area: ${u.builtUpAreaSqft === "TBD" ? "TBD" : `${formatINR(u.builtUpAreaSqft)} sqft`}\n`;
        content += `Land Area: ${u.landAreaSqft === "TBD" ? "TBD" : `${formatINR(u.landAreaSqft)} sqft`}\n`;
        content += `Premium Charges: ${u.premiumCharges}\n`;
        content += `Inclusive Price: ${u.inclusivePrice === "TBD" ? "TBD" : `₹ ${formatINR(u.inclusivePrice)}`}\n`;
        if (advRupees) content += `Advance Amount: ₹ ${formatINR(advRupees)}\n`;

        content += `\n--- APPLICANTS ---\n`;
        content += `Primary: ${u.name} | ${u.mobileNumber} | ${u.email} | PAN: ${u.pan}\n`;
        if (u.hasSecondApplicant) {
          content += `Secondary: ${u.applicant2Name || "N/A"} | PAN: ${u.applicant2Pan || "N/A"}\n`;
        }

        content += `\n--- KYC DOCUMENTS (${u.totalDocuments}) ---\n`;
        if (u.kycDocuments?.length) {
          u.kycDocuments.forEach((d, i2) => {
            content += `${i2 + 1}. ${getDocumentTypeLabel(d.type)}: ${d.url || "URL missing"}\n`;
          });
        } else {
          content += `No KYC documents found\n`;
        }

        content += `\n${"=".repeat(70)}\n\n`;
      });

      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      setDownloadStatus({ message: `Downloaded complete data (${users.length} bookings)`, type: "success" });
      setTimeout(() => setDownloadStatus({ message: "", type: "" }), 3500);
    } catch (error) {
      console.error("Data export failed:", error);
      setDownloadStatus({ message: "Failed to generate data export", type: "error" });
      setTimeout(() => setDownloadStatus({ message: "", type: "" }), 3000);
    }
  }, [users]);

  // Download selected booking details
  const handleDownloadBookingDetails = useCallback(() => {
    try {
      if (!selectedUser) return;

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const filename = `booking-details-${(selectedUser.villaNumber || selectedUser.name || "booking")
        .toString()
        .replace(/\s+/g, "-")}-${timestamp}.txt`;

      setDownloadStatus({ message: `Generating booking details for ${selectedUser.name}...`, type: "success" });

      const u = selectedUser;
      const advRupees = paiseToRupees(u.advanceAmountPaise);

      let content = `BOOKING DETAILS\n`;
      content += `Generated: ${new Date().toLocaleString("en-IN")}\n`;
      content += `======================================\n\n`;

      content += `Booking No: ${u.bookingNumber}\n`;
      content += `Payment Status: ${u.paymentStatus}\n`;
      content += `Source: ${u.source}\n`;
      if (u.sourceDetails) content += `Source Details: ${u.sourceDetails}\n`;
      content += `Created: ${u.createdAt ? u.createdAt.toLocaleString("en-IN") : "N/A"}\n`;
      if (u.updatedAt) content += `Updated: ${u.updatedAt.toLocaleString("en-IN")}\n`;

      content += `\n--- VILLA ---\n`;
      content += `Villa Number: ${u.villaNumber}\n`;
      content += `Villa ID: ${u.villaId}\n`;
      content += `Villa FirestoreId: ${u.villaFirestoreId}\n`;
      content += `Configuration: ${u.configuration}\n`;
      content += `Facing: ${u.facing}\n`;
      content += `Built-up Area: ${u.builtUpAreaSqft === "TBD" ? "TBD" : `${formatINR(u.builtUpAreaSqft)} sqft`}\n`;
      content += `Land Area: ${u.landAreaSqft === "TBD" ? "TBD" : `${formatINR(u.landAreaSqft)} sqft`}\n`;
      content += `Premium Charges: ${u.premiumCharges}\n`;
      content += `Inclusive Price: ${u.inclusivePrice === "TBD" ? "TBD" : `₹ ${formatINR(u.inclusivePrice)}`}\n`;
      if (advRupees) content += `Advance Amount: ₹ ${formatINR(advRupees)}\n`;

      content += `\n--- APPLICANTS ---\n`;
      content += `Primary: ${u.name} | ${u.mobileNumber} | ${u.email} | PAN: ${u.pan}\n`;
      if (u.hasSecondApplicant) content += `Secondary: ${u.applicant2Name || "N/A"} | PAN: ${u.applicant2Pan || "N/A"}\n`;

      content += `\n--- KYC DOCUMENTS (${u.totalDocuments}) ---\n`;
      if (u.kycDocuments?.length) {
        u.kycDocuments.forEach((d, i2) => {
          content += `${i2 + 1}. ${getDocumentTypeLabel(d.type)}: ${d.url || "URL missing"}\n`;
        });
      } else content += `No KYC documents found\n`;

      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      setDownloadStatus({ message: `Downloaded booking details for ${u.name}`, type: "success" });
      setTimeout(() => setDownloadStatus({ message: "", type: "" }), 3500);
    } catch (error) {
      console.error("Booking details export failed:", error);
      setDownloadStatus({ message: "Failed to generate booking details export", type: "error" });
      setTimeout(() => setDownloadStatus({ message: "", type: "" }), 3000);
    }
  }, [selectedUser]);

  const titleText = useMemo(() => "Clients / Bookings", []);

  return (
    <>
      {/* Download Status Notification */}
      {downloadStatus.message && (
        <div className={`md-download-status md-download-${downloadStatus.type}`}>{downloadStatus.message}</div>
      )}

      <div className="md-section-content">
        <div className="md-section-header">
          <h2 className="md-section-title">{titleText}</h2>

          <div className="md-header-actions">
            <button
              className="md-download-data-button"
              onClick={handleDownloadData}
              disabled={usersLoading || users.length === 0}
              title="Download complete booking data as TXT"
              style={{ backgroundColor: "#024837" }}
            >
              📥 Download Data
            </button>

            <button className="md-close-tab-button" onClick={clearUserSearch}>
              Close
            </button>
          </div>
        </div>

        <div className="md-search-container">
          <input
            type="text"
            placeholder='Search: "Prashant", "Villa - 24", "villa-24", "Success", "VILLA/2025..."'
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            className="md-search-input"
            onKeyDown={(e) => e.key === "Enter" && handleUserSearch()}
          />

          <button className="md-search-button" onClick={handleUserSearch} disabled={userSearchQuery.trim() === ""}>
            Search
          </button>

          <button className="md-clear-button" onClick={clearUserSearch} disabled={userSearchQuery.trim() === ""}>
            Clear
          </button>
        </div>

        {usersLoading ? (
          <div className="md-loading">Loading...</div>
        ) : usersError ? (
          <div className="md-error">{usersError}</div>
        ) : filteredUsers.length === 0 ? (
          <p className="md-no-results">No bookings found.</p>
        ) : (
          <div className="md-users-table-container md-scrollable-table">
            <table className="md-users-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Villa</th>
                  <th>Booking No</th>
                  <th>Source</th>
                  <th>Payment</th>
                </tr>
              </thead>

              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.bookingFirestoreId} onClick={() => openUserDetailsModal(u)} className="md-user-row">
                    <td>{u.createdAtDate}</td>

                    <td>
                      {u.name || "N/A"}
                      {u.hasSecondApplicant && (
                        <>
                          <br />
                          <small style={{ color: "#666" }}>{u.applicant2Name || "Applicant 2"}</small>
                        </>
                      )}
                    </td>

                    <td>{u.mobileNumber || "N/A"}</td>

                    <td>
                      {u.villaNumber || "N/A"}
                      <br />
                      <small style={{ color: "#666" }}>{u.villaId || ""}</small>
                    </td>

                    <td>{u.bookingNumber || "N/A"}</td>

                    <td>{u.source || "N/A"}</td>

                    <td>
                      <span className={`md-booking-status ${paymentStatusClass(u.paymentStatus)}`}>
                        {u.paymentStatus || "N/A"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* -------------------- Booking Details Modal -------------------- */}
        {selectedUser && (
          <div className="md-modal-overlay" onMouseDown={(e) => e.target.classList.contains("md-modal-overlay") && closeUserDetailsModal()}>
            <div className="md-modal-content md-user-details-modal">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 className="md-modal-title">
                  Booking Details - {selectedUser.name || "N/A"}
                </h3>

                <span className="md-booking-badge">
                  {selectedUser.villaNumber || "N/A"}
                </span>
              </div>

              <div className="md-user-details-content" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {/* Left */}
                <div className="md-user-details-column">
                  <p><strong>Primary Applicant:</strong> {selectedUser.name || "N/A"}</p>
                  <p><strong>Primary PAN:</strong> {selectedUser.pan || "N/A"}</p>

                  {selectedUser.hasSecondApplicant && (
                    <>
                      <p><strong>Secondary Applicant:</strong> {selectedUser.applicant2Name || "N/A"}</p>
                      <p><strong>Secondary PAN:</strong> {selectedUser.applicant2Pan || "N/A"}</p>
                    </>
                  )}

                  <p><strong>Mobile:</strong> {selectedUser.mobileNumber || "N/A"}</p>
                  <p><strong>Email:</strong> {selectedUser.email || "N/A"}</p>

                  <p><strong>Booking Number:</strong> {selectedUser.bookingNumber || "N/A"}</p>
                  <p><strong>Created:</strong> {selectedUser.createdAt ? selectedUser.createdAt.toLocaleString("en-IN") : "N/A"}</p>
                  <p><strong>Updated:</strong> {selectedUser.updatedAt ? selectedUser.updatedAt.toLocaleString("en-IN") : "N/A"}</p>

                  <p><strong>Source:</strong> {selectedUser.source || "N/A"}</p>
                  {selectedUser.sourceDetails ? <p><strong>Source Details:</strong> {selectedUser.sourceDetails}</p> : null}
                </div>

                {/* Right */}
                <div className="md-user-details-column">
                  <p><strong>Payment Status:</strong> {" "}
                    <span className={`md-booking-status ${paymentStatusClass(selectedUser.paymentStatus)}`}>
                      {selectedUser.paymentStatus || "N/A"}
                    </span>
                  </p>

                  <p><strong>Advance Amount:</strong> {selectedUser.advanceAmountPaise ? `₹ ${formatINR(paiseToRupees(selectedUser.advanceAmountPaise))}` : "N/A"}</p>

                  <hr style={{ margin: "10px 0", borderColor: "#eee" }} />

                  <p><strong>Villa Number:</strong> {selectedUser.villaNumber || "N/A"}</p>
                  <p><strong>Villa ID:</strong> {selectedUser.villaId || "N/A"}</p>
                  <p><strong>Villa FirestoreId:</strong> {selectedUser.villaFirestoreId || "N/A"}</p>

                  <p><strong>Configuration:</strong> {selectedUser.configuration || "Unknown"}</p>
                  <p><strong>Facing:</strong> {selectedUser.facing || "Unknown"}</p>

                  <p><strong>Built-up Area:</strong> {selectedUser.builtUpAreaSqft === "TBD" ? "TBD" : `${formatINR(selectedUser.builtUpAreaSqft)} sqft`}</p>
                  <p><strong>Land Area:</strong> {selectedUser.landAreaSqft === "TBD" ? "TBD" : `${formatINR(selectedUser.landAreaSqft)} sqft`}</p>

                  <p><strong>Premium Charges:</strong> {selectedUser.premiumCharges ?? "TBD"}</p>
                  <p><strong>Inclusive Price:</strong> {selectedUser.inclusivePrice === "TBD" ? "TBD" : `₹ ${formatINR(selectedUser.inclusivePrice)}`}</p>

                  {selectedUser.kycDocuments?.length ? (
                    <div style={{ marginTop: 10 }}>
                      <p style={{ marginBottom: 6 }}><strong>KYC Documents:</strong></p>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {selectedUser.kycDocuments.map((d, i) => (
                          <li key={i} style={{
                            background: "#e8f5e8",
                            color: "#2e7d32",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 12,
                            margin: "2px 0",
                            borderLeft: "3px solid #4CAF50",
                          }}>
                            {getDocumentTypeLabel(d.type)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="md-modal-actions">
                <button
                  className="md-download-plot-button"
                  onClick={handleDownloadBookingDetails}
                  title="Download booking details as TXT"
                  style={{
                    borderRadius: 10,
                    border: "none",
                    backgroundColor: "#2196F3",
                    color: "white",
                    marginRight: 10,
                    padding: "8px 14px",
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  📥 Download Data
                </button>

                {selectedUser.kycDocuments?.length ? (
                  <button
                    className="md-view-documents-button"
                    onClick={() => openDocumentsModal(selectedUser.kycDocuments, selectedUser)}
                    style={{
                      borderRadius: 10,
                      border: "none",
                      backgroundColor: "blue",
                      color: "white",
                      padding: "8px 14px",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    📄 View All Documents ({selectedUser.totalDocuments})
                  </button>
                ) : null}

                <button className="md-modal-button md-cancel" onClick={closeUserDetailsModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* -------------------- Documents Modal -------------------- */}
        {selectedDocuments.length > 0 && selectedUser && (
          <div className="md-modal-overlay" onMouseDown={(e) => e.target.classList.contains("md-modal-overlay") && closeDocumentsModal()}>
            <div className="md-modal-content md-documents-modal">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <h3 className="md-modal-title" style={{ margin: 0 }}>
                  KYC Documents ({selectedDocuments.length}) - {selectedUser.name}
                </h3>

                <button className="md-modal-button md-cancel" onClick={closeDocumentsModal} style={{ marginLeft: "auto" }}>
                  Close
                </button>
              </div>

              <div className="md-documents-grid">
                {selectedDocuments.map((d, i) => (
                  <div key={i} className="md-document-card">
                    <div className="md-document-thumbnail">
                      <img
                        src={d.url}
                        alt={getDocumentTypeLabel(d.type)}
                        className="md-thumbnail-image"
                        onError={(e) => {
                          e.target.src = "/placeholder-image.png";
                          e.target.alt = "Image not available";
                        }}
                        loading="lazy"
                      />
                      <div className="md-document-icon-overlay">{getDocumentTypeIcon(d.type)}</div>
                    </div>

                    <div className="md-document-info">
                      <h4 style={{ margin: "8px 0" }}>{getDocumentTypeLabel(d.type)}</h4>

                      <div className="md-document-actions-small">
                        <button className="md-view-button" onClick={() => openDocumentInNewTab(d.url)} title="View in new tab">
                          👁️ View
                        </button>

                        <button
                          className="md-download-button-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const filename = `${getDocumentTypeLabel(d.type)}-${Date.now()}.${getFileExtension(d.url)}`;
                            handleDownload(d.url, filename);
                          }}
                          title="Download document"
                        >
                          💾 Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedDocuments.length > 1 && (
                <div className="md-modal-actions">
                  <button
                    className="md-download-all-button"
                    onClick={handleDownloadAll}
                    disabled={downloadStatus.message && downloadStatus.type === "success"}
                  >
                    💾 Download All ({selectedDocuments.length})
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ✅ CRA-safe styles (no style jsx) */}
      <style>{`
        .md-download-status {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #4CAF50;
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          z-index: 10000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          font-weight: 700;
          max-width: 320px;
          word-wrap: break-word;
          animation: slideIn 0.25s ease-out;
        }
        .md-download-error { background: #f44336 !important; }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Payment status pills */
        .md-booking-status.success {
          background: rgba(2, 72, 55, 0.12);
          color: #024837;
          border: 1px solid rgba(2, 72, 55, 0.18);
        }
        .md-booking-status.failed {
          background: rgba(255, 0, 0, 0.10);
          color: #b91c1c;
          border: 1px solid rgba(185, 28, 28, 0.18);
        }
        .md-booking-status.pending {
          background: rgba(179, 137, 0, 0.12);
          color: #b38900;
          border: 1px solid rgba(179, 137, 0, 0.22);
        }
        .md-booking-status.unknown {
          background: rgba(107, 114, 128, 0.15);
          color: #374151;
          border: 1px solid rgba(107, 114, 128, 0.22);
        }

        /* Documents grid cards */
        .md-documents-grid{
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }
        .md-document-card{
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 10px;
          background: #fff;
          transition: transform 0.2s ease;
        }
        .md-document-card:hover{ transform: translateY(-2px); }
        .md-document-thumbnail{
          position: relative;
          overflow: hidden;
          border-radius: 10px;
        }
        .md-thumbnail-image{
          width: 100%;
          height: 150px;
          object-fit: cover;
          border-radius: 10px;
          display: block;
        }
        .md-document-icon-overlay{
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(0,0,0,0.7);
          color: white;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }
        .md-document-actions-small{
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .md-view-button, .md-download-button-small, .md-download-all-button{
          border: none;
          border-radius: 10px;
          padding: 8px 10px;
          font-weight: 800;
          cursor: pointer;
        }
        .md-view-button{ background: rgba(26,60,52,0.10); }
        .md-download-button-small{ background: rgba(33,150,243,0.12); }
        .md-download-all-button{ background: #1a3c34; color: #fff; }

        .md-booking-badge{
          background: rgba(26,60,52,0.10);
          color: #1a3c34;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 12px;
          border: 1px solid rgba(26,60,52,0.18);
          white-space: nowrap;
        }

        @media (max-width: 900px){
          .md-user-details-content{ grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
};

export default Users;
